(function () {
  const BUNDLE_ID_PROPERTY = '_bundle_id';
  const SERVICE_FLAG_PROPERTY = '_is_engraving_service';
  const SERVICE_FLAG_VALUE = 'true';
  const CART_REFRESH_EVENT = 'theme:cart:refresh';
  const CART_CHANGE_PATH = '/cart/change';
  const CART_UPDATE_PATH = '/cart/update';
  const CART_PAGE_PATH = '/cart';
  const JSON_CONTENT_TYPE = 'application/json';

  let bundleStateCache = {};
  let validationPromise = null;
  let validationRequested = false;
  let pendingCartPromise = null;

  function getBundleId(item) {
    return item?.properties?.[BUNDLE_ID_PROPERTY];
  }

  function isEngravingService(item) {
    return item?.properties?.[SERVICE_FLAG_PROPERTY] === SERVICE_FLAG_VALUE;
  }

  function buildBundleMap(cart) {
    const bundleMap = {};

    for (const item of cart?.items || []) {
      const bundleId = getBundleId(item);
      if (!bundleId) {
        continue;
      }

      if (!bundleMap[bundleId]) {
        bundleMap[bundleId] = { main: null, service: null };
      }

      if (isEngravingService(item)) {
        bundleMap[bundleId].service = item;
      } else {
        bundleMap[bundleId].main = item;
      }
    }

    return bundleMap;
  }

  function seedBundleState(bundleMap) {
    const nextState = {};

    for (const [bundleId, pair] of Object.entries(bundleMap)) {
      if (!pair.main || !pair.service) {
        continue;
      }

      nextState[bundleId] = {
        mainQty: pair.main.quantity,
        serviceQty: pair.service.quantity,
      };
    }

    return nextState;
  }

  function computeBundleUpdates(bundleMap, previousState) {
    const updates = {};

    for (const [bundleId, pair] of Object.entries(bundleMap)) {
      if (pair.service && !pair.main) {
        updates[pair.service.key] = 0;
        continue;
      }

      if (pair.main && !pair.service) {
        updates[pair.main.key] = 0;
        continue;
      }

      if (!pair.main || !pair.service || pair.main.quantity === pair.service.quantity) {
        continue;
      }

      const previousPairState = previousState[bundleId];
      if (!previousPairState) {
        updates[pair.service.key] = pair.main.quantity;
        continue;
      }

      const mainQtyChanged = previousPairState.mainQty !== pair.main.quantity;
      const serviceQtyChanged = previousPairState.serviceQty !== pair.service.quantity;

      if (mainQtyChanged) {
        updates[pair.service.key] = pair.main.quantity;
      } else if (serviceQtyChanged) {
        updates[pair.main.key] = pair.service.quantity;
      }
    }

    return updates;
  }

  async function fetchCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js');
    return response.json();
  }

  async function applyCartUpdates(updates) {
    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': JSON_CONTENT_TYPE,
      },
      body: JSON.stringify({ updates }),
    });
    const updatedCart = await getCartFromResponse(response, response?.url || CART_UPDATE_PATH);

    document.dispatchEvent(
      new CustomEvent(CART_REFRESH_EVENT, {
        bubbles: true,
        detail: {
          cart: updatedCart,
        },
      })
    );

    if (window.location.pathname === CART_PAGE_PATH) {
      window.location.reload();
    }
  }

  async function validateEngravingCart(cart) {
    try {
      const resolvedCart = cart || (await fetchCart());
      const bundleMap = buildBundleMap(resolvedCart);
      const updates = computeBundleUpdates(bundleMap, bundleStateCache);
      bundleStateCache = seedBundleState(bundleMap);

      if (Object.keys(updates).length === 0) {
        return;
      }

      await applyCartUpdates(updates);
    } catch (error) {
      console.error('Cart Validation Error:', error);
    }
  }

  function requestCartValidation(cartPromise = null) {
    validationRequested = true;
    if (cartPromise) {
      pendingCartPromise = cartPromise;
    }

    if (validationPromise) {
      return validationPromise;
    }

    validationPromise = (async () => {
      while (validationRequested) {
        validationRequested = false;
        const nextCartPromise = pendingCartPromise;
        pendingCartPromise = null;
        const nextCart = nextCartPromise ? await nextCartPromise : null;
        await validateEngravingCart(nextCart);
      }
    })().finally(() => {
      validationPromise = null;
    });

    return validationPromise;
  }

  function shouldValidateCart(responseUrl) {
    return (
      responseUrl.includes('/cart/add') ||
      responseUrl.includes(CART_CHANGE_PATH) ||
      responseUrl.includes(CART_UPDATE_PATH)
    );
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') {
      return input;
    }

    if (input instanceof URL) {
      return input.href;
    }

    if (input && typeof input.url === 'string') {
      return input.url;
    }

    return '';
  }

  function canUseResponseCart(responseUrl) {
    return responseUrl.includes(CART_CHANGE_PATH) || responseUrl.includes(CART_UPDATE_PATH);
  }

  async function getCartFromResponse(response, responseUrl) {
    if (!response || typeof response.clone !== 'function' || !canUseResponseCart(responseUrl)) {
      return null;
    }

    try {
      const responseCart = await response.clone().json();

      if (Array.isArray(responseCart?.items)) {
        return responseCart;
      }
    } catch (error) {
      console.warn('Cart Validation Snapshot Error:', error);
    }

    return null;
  }

  function patchWindowFetch() {
    const originalFetch = window.fetch;

    window.fetch = async function patchedFetch(...args) {
      const requestUrl = getRequestUrl(args[0]);
      const response = await originalFetch.apply(this, args);
      const responseUrl = response?.url || requestUrl;

      if (shouldValidateCart(responseUrl || '')) {
        requestCartValidation(getCartFromResponse(response, responseUrl));
      }

      return response;
    };

    if (globalThis.fetch !== window.fetch) {
      globalThis.fetch = (...args) => window.fetch(...args);
    }
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const shouldValidateOnLoad = window.BelvansCartIntegrityValidatorConfig?.shouldValidateOnLoad !== false;

    document.addEventListener('DOMContentLoaded', () => {
      if (!shouldValidateOnLoad) {
        return;
      }

      requestCartValidation();
    });
    document.addEventListener(CART_REFRESH_EVENT, (event) => requestCartValidation(Promise.resolve(event?.detail?.cart || null)));
    patchWindowFetch();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildBundleMap,
      computeBundleUpdates,
      getCartFromResponse,
      seedBundleState,
      getRequestUrl,
      requestCartValidation,
      shouldValidateCart,
    };
  }
})();
